/**
 * Tipo de programa de educación continua (del CeStudyPlan)
 */
export enum ContinuousEducationType {
  COURSE = 'course',
  PROGRAM_COURSE = 'program_course',
}

/**
 * Estado de la oferta de educación continua
 */
export enum CeAcademicOfferStatus {
  Borrador = 'Borrador',
  Activa = 'Activa',
  Cerrada = 'Cerrada',
}

/**
 * Interfaz para TuitionProcessType
 */
export interface TuitionProcessType {
  id: number;
  name: string;
  code: string;
}

/**
 * Interfaz para TuitionProcess
 */
export interface TuitionProcess {
  id: number;
  name: string;
  tuitionProcessType?: TuitionProcessType;
}

/**
 * Interfaz para TuitionSubprocess
 */
export interface TuitionSubprocess {
  id: number;
  type: string;
  process?: TuitionProcess;
}

/**
 * Interfaz para StudyPlanVersionOfferPeriodAdmission
 */
export interface StudyPlanVersionOfferPeriodAdmission {
  id: number;
  quota: number | null;
  subprocess?: TuitionSubprocess;
}

/**
 * Interfaz para Modality
 */
export interface Modality {
  id: number;
  name: string;
  code: string;
  deliveryMode: string;
}

/**
 * Interfaz para StudyPlan (plan de estudio de EC)
 */
export interface StudyPlan {
  id: number;
  name: string;
  code: string;
  school?: { id: number; name: string };
  modality?: Modality;
  studyPlanCe?: {
    id: number;
    type: ContinuousEducationType;
  };
}

/**
 * Interfaz para StudyPlanVersionOffer
 */
export interface StudyPlanVersionOffer {
  id: number;
  campusId: number;
  scheduleId: number;
  campus?: {
    id: number;
    name: string;
  };
  schedule?: {
    id: number;
    name: string;
  };
  studyPlanVersion?: {
    id: number;
    name: string;
    studyPlan?: StudyPlan;
  };
}

/**
 * Interfaz para AcademicOfferStudyPlanVersionOffer (relación intermedia)
 */
export interface AcademicOfferStudyPlanVersionOffer {
  id: number;
  quota: number | null;
  completionDaysLimit: number | null;
  startDate?: string | null;
  studyPlanVersionOffer?: StudyPlanVersionOffer;
  admissions?: StudyPlanVersionOfferPeriodAdmission[];
}

/**
 * Interfaz para la oferta de educación continua (CeAcademicOffer)
 */
export interface CeAcademicOffer {
  id: number;
  status: CeAcademicOfferStatus;
  firstInstallmentRuleId: number | null;
  createdAt: string;
  updatedAt: string;
  enrolled?: number;
  academicOfferStudyPlanVersionOffers?: AcademicOfferStudyPlanVersionOffer[];
}

/**
 * Datos del formulario para crear/editar oferta
 */
export interface CeAcademicOfferFormData {
  campuses: Array<{ id: number; name: string }>;
  studyPlans: Array<StudyPlan>;
  modalities: Array<Modality>;
  processTypes: Array<TuitionProcessType>;
}

/**
 * Filtros para la búsqueda de ofertas
 */
export interface CeAcademicOfferFilters {
  processTypeId?: number;
  programType?: ContinuousEducationType;
  studyPlanId?: number;
  modalityId?: number;
  campusId?: number;
  status?: CeAcademicOfferStatus;
  page?: number;
  items_per_page?: number;
}

/**
 * Respuesta paginada de ofertas
 */
export interface CeAcademicOfferPaginatedResponse {
  data: CeAcademicOffer[];
  total: number;
  total_pages: number;
}

/**
 * Payload para crear una oferta de educación continua
 */
export interface CreateCeAcademicOfferPayload {
  studyPlanId: number;
  campusId: number;
  quota?: number;
  completionDaysLimit?: number;
}

/**
 * Payload para actualizar una oferta de educación continua
 */
export interface UpdateCeAcademicOfferPayload {
  firstInstallmentRuleId?: number | null;
  quota?: number;
  completionDaysLimit?: number | null;
}

/**
 * Enum de pasos para la creación de oferta académica de EC
 */
export enum CeAcademicOfferStepEnum {
  Offer = 'offer',
  Sections = 'sections',
  Schedule = 'schedule',
  Summary = 'summary',
}

/**
 * Respuesta del endpoint de paso actual
 */
export interface CeAcademicOfferStepResponse {
  id: number;
  ceAcademicOfferId: number;
  currentStep: CeAcademicOfferStepEnum;
  createdAt: string;
  updatedAt: string;
}
